<script lang="ts">
	import "@assets/css/flags.css";
	import { TelInput, normalizedCountries } from "svelte-tel-input";
	import type { CountryCode, E164Number } from "svelte-tel-input/types";
	import { Locale } from "@core/schema/Locale.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	let {
		/* eslint-disable-next-line prefer-const */
		id,
		/* eslint-disable-next-line prefer-const */
		required = false,
		value = $bindable(null),
		input = $bindable(null),
	} = $props<{
		id: string;
		required?: boolean;
		value?: E164Number | null;
		input?: HTMLInputElement | null;
	}>();

	const { tenant } = getGlobalTemplateProps();

	const localeToCountryCode: Record<Locale, CountryCode> = {
		[Locale.cs]: "CZ",
		[Locale.sk]: "SK",
		[Locale.en]: "US",
	};

	const searchPlaceholder: Record<Locale, string> = {
		[Locale.cs]: "Vyhledat zemi...",
		[Locale.en]: "Search country...",
		[Locale.sk]: "Vyhľadať krajinu...",
	};

	const noResultsText: Record<Locale, string> = {
		[Locale.cs]: "Žádné výsledky",
		[Locale.en]: "No results",
		[Locale.sk]: "Žiadne výsledky",
	};

	const invalidPhoneMessage: Record<Locale, string> = {
		[Locale.cs]: "Zadejte platné telefonní číslo.",
		[Locale.en]: "Please enter a valid phone number.",
		[Locale.sk]: "Zadajte platné telefónne číslo.",
	};

	const requiredMessage: Record<Locale, string> = {
		[Locale.cs]: "Toto pole je povinné.",
		[Locale.en]: "This field is required.",
		[Locale.sk]: "Toto pole je povinné.",
	};

	const defaultCountry = localeToCountryCode[tenant.locale];
	let selectedCountry: CountryCode = $state(defaultCountry);
	let isDropdownOpen = $state(false);
	let searchQuery = $state("");
	let dropdownRef = $state<HTMLDivElement | null>(null);
	let errorMessage = $state("");
	let hasBeenValidated = $state(false);

	const selectedCountryData = $derived.by(() => {
		const found = normalizedCountries.find((country) => country.iso2 === selectedCountry);
		return found ?? normalizedCountries[0];
	});

	function toE164(rawValue: string): E164Number | null {
		const cleaned = rawValue.replace(/[\s\-()]/gu, "");
		if (cleaned.length === 0) {
			return null;
		}
		if (cleaned.startsWith("+")) {
			return cleaned as E164Number;
		}
		if (cleaned.startsWith("00")) {
			return `+${cleaned.slice(2)}` as E164Number;
		}
		const dialCode = selectedCountryData?.dialCode;
		if (dialCode === undefined) {
			return null;
		}
		return `+${String(dialCode)}${cleaned}` as E164Number;
	}

	function syncValueFromDom(): void {
		if (!input) {
			return;
		}
		const container = input.parentElement;
		if (!container) {
			return;
		}
		const telEl: HTMLInputElement | null = container.querySelector('input[type="tel"]');
		if (!telEl) {
			return;
		}
		value = toE164(telEl.value);
	}

	function handleTelInput(): void {
		syncValueFromDom();
	}

	const isPhoneValid = $derived.by(() => {
		if (!value) {
			return !required;
		}
		const cleaned = value.replace(/[\s\-()]/gu, "");
		return /^\+[1-9]\d{6,14}$/u.test(cleaned);
	});

	const filteredCountries = $derived(
		normalizedCountries.filter(
			(country) =>
				country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
				country.iso2.toLowerCase().includes(searchQuery.toLowerCase()) ||
				String(country.dialCode).includes(searchQuery),
		),
	);

	function selectCountry(countryCode: CountryCode): void {
		selectedCountry = countryCode;
		isDropdownOpen = false;
		searchQuery = "";
	}

	function handleClickOutside(event: MouseEvent): void {
		if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
			isDropdownOpen = false;
			searchQuery = "";
		}
	}

	$effect(() => {
		if (input) {
			if (required && (!value || value === "")) {
				input.setCustomValidity(requiredMessage[tenant.locale]);
				if (hasBeenValidated) {
					errorMessage = requiredMessage[tenant.locale];
				}
			} else if (value && !isPhoneValid) {
				input.setCustomValidity(invalidPhoneMessage[tenant.locale]);
				if (hasBeenValidated) {
					errorMessage = invalidPhoneMessage[tenant.locale];
				}
			} else {
				input.setCustomValidity("");
				errorMessage = "";
			}
		}
	});

	$effect(() => {
		if (typeof document !== "undefined" && isDropdownOpen) {
			document.addEventListener("click", handleClickOutside);
			return (): void => {
				document.removeEventListener("click", handleClickOutside);
			};
		}
		return undefined;
	});

	$effect(() => {
		if (!input) {
			return undefined;
		}

		const container = input.parentElement;
		if (!container) {
			return undefined;
		}

		const telInputEl: HTMLInputElement | null = container.querySelector('input[type="tel"]');
		if (!telInputEl) {
			return undefined;
		}

		function onAutofill(e: AnimationEvent): void {
			if (e.animationName !== "onAutoFillStart") {
				return;
			}
			setTimeout(() => {
				syncValueFromDom();
			}, 50);
		}

		input.addEventListener("animationstart", onAutofill as EventListener);
		telInputEl.addEventListener("animationstart", onAutofill as EventListener);

		const currentInput = input;
		return (): void => {
			currentInput.removeEventListener("animationstart", onAutofill as EventListener);
			telInputEl.removeEventListener("animationstart", onAutofill as EventListener);
		};
	});
</script>

<div class="relative">
	<div
		class={[
			"relative flex w-full items-center min-w-0 overflow-hidden rounded-[.25rem] border transition-[outline,text,border] duration-300 focus-within:border-zms-blue outline-1 outline outline-offset-1 min-h-[3.125rem] bg-white",
			hasBeenValidated && (!isPhoneValid || (required && !value))
				? "border-red-300 outline-red-300"
				: "border-[#D7D7D7] outline-transparent",
		]}
	>
		<div class="relative" bind:this={dropdownRef}>
			<button
				type="button"
				class="flex items-center gap-2 px-2 lg:px-3 min-h-[3.125rem]"
				aria-label="{selectedCountryData?.name ?? 'Select country'} +{selectedCountryData?.dialCode ?? ''}"
				onclick={(): void => {
					isDropdownOpen = !isDropdownOpen;
				}}
			>
				<span class="flag flag-{selectedCountry.toLowerCase()} flex-shrink-0"></span>
				<span class="font-semibold text-[#595959] text-sm lg:text-base">+{selectedCountryData?.dialCode ?? ""}</span>
				<svg
					class="w-4 h-4 text-gray-400 transition-transform {isDropdownOpen ? 'rotate-180' : ''}"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
				</svg>
			</button>
		</div>

		<TelInput
			bind:country={selectedCountry}
			{value}
			class="flex-1 !border-0 !outline-0 !shadow-none !ring-0 !p-2 md:!p-7 !min-h-[3.125rem] font-semibold md:text-[1.125rem]/[1.2] focus:text-zms-optical-black text-[#595959] focus:!outline-none focus:!ring-0 focus:!shadow-none {!isPhoneValid &&
			hasBeenValidated
				? '!border-red-300'
				: ''}"
			on:input={handleTelInput}
			on:change={handleTelInput}
		/>

		<input
			bind:this={input}
			{id}
			name={id}
			class="absolute inset-0 pointer-events-none opacity-0"
			value={value ?? ""}
			{required}
			autocomplete="tel"
			oninvalid={(event): void => {
				event.preventDefault();
				hasBeenValidated = true;
				if (required && (!value || value === "")) {
					errorMessage = requiredMessage[tenant.locale];
				} else if (!isPhoneValid) {
					errorMessage = invalidPhoneMessage[tenant.locale];
				}
			}}
		/>
	</div>

	{#if errorMessage}
		<span class="text-red-500 text-xs mt-1">{errorMessage}</span>
	{/if}

	{#if isDropdownOpen}
		<div
			class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] w-80 max-h-96 flex flex-col"
		>
			<div class="p-2 border-b border-gray-200">
				<label for="country-search" class="sr-only">{searchPlaceholder[tenant.locale]}</label>
				<input
					id="country-search"
					type="text"
					bind:value={searchQuery}
					placeholder={searchPlaceholder[tenant.locale]}
					class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zms-blue focus:border-transparent"
					onclick={(e: MouseEvent): void => {
						e.stopPropagation();
					}}
				/>
			</div>
			<div class="overflow-y-auto max-h-80">
				{#each filteredCountries as country (country.iso2)}
					<button
						type="button"
						class="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors text-left {country.iso2 ===
						selectedCountry
							? 'bg-blue-50'
							: ''}"
						onclick={(): void => {
							selectCountry(country.iso2);
						}}
					>
						<span class="flag flag-{country.iso2.toLowerCase()} flex-shrink-0"></span>
						<span class="flex-1 text-sm">{country.name}</span>
						<span class="text-sm text-gray-500">+{country.dialCode}</span>
					</button>
				{/each}
				{#if filteredCountries.length === 0}
					<div class="px-4 py-8 text-center text-gray-500 text-sm">{noResultsText[tenant.locale]}</div>
				{/if}
			</div>
		</div>
	{/if}
</div>

<style>
	:global(input:-webkit-autofill) {
		animation-name: onAutoFillStart;
		animation-duration: 1ms;
	}

	@keyframes onAutoFillStart {
		from {
			opacity: 0.9999;
		}
		to {
			opacity: 1;
		}
	}
</style>
